import type { FuseResultWithScore } from 'fuse.js';
import { colorSimilarity } from './colorSimilarity';
import { cosineSimilarity } from './cosineSimilarity';
import type { Embedding, ThemeEmbeddings } from './search.types';

export const SIMILARITY_THRESHOLD = 0.44;

type ScoreAndFilterOptions<T, F = T> = Readonly<{
  termEmbedding: Embedding;
  themes: Map<string, ThemeEmbeddings>;
  embeddingMap: Map<string, Embedding>;
  getThemeId: (item: T) => string;
  fuseResults: ReadonlyArray<FuseResultWithScore<F>>;
  getFuseItemThemeId: (item: F) => string;
  threshold: number;
  wantsFree?: boolean;
  isFreeItem?: (item: T) => boolean;
}>;

export function scoreAndFilterByEmbedding<T, F = T>(
  items: ReadonlyArray<T>,
  { termEmbedding, themes, embeddingMap, getThemeId, fuseResults, getFuseItemThemeId, threshold, wantsFree = false, isFreeItem }: ScoreAndFilterOptions<T, F>,
): ReadonlyArray<T> {
  const white = embeddingMap.get('color: white');
  const beige = embeddingMap.get('color: beige');

  const lexicalScores = new Map(fuseResults.map(r => [getFuseItemThemeId(r.item), 1 - (r.score ?? 1)]));
  const similarityMap = new Map<string, number>();

  for (const item of items) {
    const themeId = getThemeId(item);
    const theme = themes.get(themeId);

    const similarityLexical = lexicalScores.get(themeId) ?? 0;
    const similarityConcept = Math.max(0, ...(theme?.concepts ?? []).map(e => cosineSimilarity(termEmbedding, e)));
    const similarityCustom = Math.max(0, ...(theme?.customs ?? []).map(e => cosineSimilarity(termEmbedding, e)));
    const similarityColor = colorSimilarity(termEmbedding, theme?.colors ?? [], white, beige);
    const isFree = wantsFree && (isFreeItem?.(item) ?? false);

    const similarity = Math.max(
      similarityLexical * 1.01, // A perfect lexical match ranks above all else. *1.01 to tie-break.
      similarityConcept,
      similarityColor,
      similarityCustom,
      isFree ? 1 : 0,
    );

    similarityMap.set(themeId, similarity);
  }

  const filtered = items.filter(item => (similarityMap.get(getThemeId(item)) ?? 0) >= threshold);
  return [...filtered].sort((a, b) => (similarityMap.get(getThemeId(b)) ?? 0) - (similarityMap.get(getThemeId(a)) ?? 0));
}
